// Selling a bought or traded-in phone wholesale: who's buying, for how much, and how they paid.
// The phone leaves inventory as sold, the buyer, price and payment stay on its record, cash goes
// into the register's drawer, and the profit shows in the owner's reports. Wholesale prices are the
// owner's: anyone else sells or undoes one with the owner's PIN.
import { useState } from 'react';
import { get, post } from '../lib/api';
import { useLoad } from '../lib/hooks';
import { useSession } from '../lib/session';
import { formatCents } from '../../shared/money';
import { formatDateTime } from '../../shared/format';
import { WHOLESALE_PAYS, WHOLESALE_PAY_LABELS, type WholesalePay } from '../../shared/buyback';
import { Button, Cost, Field, MoneyInput, Profit, Sheet, Spinner, useAction, type ApprovalInput } from '../ui/kit';
import { Icon } from '../ui/icons';

/** Phones that can still go wholesale: not sold, not deleted, not on the way to another store. */
export const WHOLESALE_STATUSES = ['testing', 'on_hold', 'in_stock', 'parts_only'];

/** The box for the payment's reference: the Zelle confirmation, the check number, or what "other" was. */
export function wholesaleRefLabel(paidBy: WholesalePay | ''): string | null {
  if (paidBy === 'zelle') return 'Zelle confirmation (optional)';
  if (paidBy === 'check') return 'Check number (optional)';
  if (paidBy === 'card') return 'Card note (optional)';
  if (paidBy === 'other') return 'How they paid';
  return null;
}

/** How the wholesale buyer paid: Cash, Zelle, Card, Check or Other, and what it means for the drawer. */
export function WholesalePayPicker({ paidBy, paidRef, onPaidBy, onPaidRef, label = 'How the buyer paid' }: {
  paidBy: WholesalePay | ''; paidRef: string; onPaidBy: (v: WholesalePay) => void; onPaidRef: (v: string) => void; label?: string;
}) {
  const { me } = useSession();
  const refLabel = wholesaleRefLabel(paidBy);
  return (
    <div className="stack-sm">
      <span className="small strong">{label}</span>
      <div className="chips" role="group" aria-label={label}>
        {WHOLESALE_PAYS.map((m) => <button key={m} type="button" className="chip" aria-pressed={paidBy === m} onClick={() => onPaidBy(m)}>{WHOLESALE_PAY_LABELS[m]}</button>)}
      </div>
      {paidBy === 'cash' ? (
        <p className={`small ${me?.register ? 'muted' : 'error-text'}`} style={{ margin: 0 }}>
          {me?.register ? 'The cash goes into this register’s drawer (its drawer has to be open).' : 'Cash goes into a register’s drawer: do this on the store tablet.'}
        </p>
      ) : paidBy ? <p className="small muted" style={{ margin: 0 }}>Recorded with the wholesale; nothing goes in the drawer.</p> : null}
      {refLabel ? <Field label={refLabel}><input className="input" value={paidRef} onChange={(e) => onPaidRef(e.target.value)} placeholder={paidBy === 'other' ? 'e.g. Venmo' : undefined} /></Field> : null}
    </div>
  );
}

export function WholesaleSheet({ deviceId, onClose, onDone }: { deviceId: string; onClose: () => void; onDone: (device: any) => void }) {
  const { me, can } = useSession();
  const d = useLoad<any>(() => get(`/api/devices/${deviceId}`), [deviceId]);
  const buyers = useLoad<{ buyers: string[] }>(() => get('/api/wholesale/buyers'), []);
  const { run, busy } = useAction();
  const [buyer, setBuyer] = useState('');
  const [price, setPrice] = useState<number | null>(null);
  const [paidBy, setPaidBy] = useState<WholesalePay | ''>('');
  const [paidRef, setPaidRef] = useState('');
  const [note, setNote] = useState('');
  const x = d.data;
  const held = !!x?.hold_until && x.hold_until > Date.now();
  const cost: number | null = x?.cost_cents ?? null;
  const payReady = !!paidBy && !(paidBy === 'other' && !paidRef.trim()) && !(paidBy === 'cash' && !me?.register);
  const save = async () => {
    const r = await run(
      (approval?: ApprovalInput) => post(`/api/devices/${deviceId}/wholesale`, { buyer: buyer.trim(), priceCents: price, paidBy, paidRef: paidRef.trim() || null, note: note.trim() || null, approval }),
      { success: paidBy === 'cash' ? 'Sold wholesale. The cash is in the drawer.' : 'Sold wholesale.' },
    );
    if (r) onDone(r);
  };
  return (
    <Sheet
      title="Sell wholesale"
      onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" icon="truck" busy={busy} disabled={!x || buyer.trim().length < 2 || !price || held || !payReady} onClick={save}>Sell wholesale</Button></>}
    >
      {d.error ? <p className="muted">{d.error.message}</p> : !x ? <Spinner /> : (
        <div className="stack-sm">
          <p className="muted">{x.title} goes to a wholesale buyer instead of the store’s shelf. It leaves inventory as sold{can('wholesale.sell') ? ', and the profit shows in Reports.' : '. Wholesale prices are the owner’s: the owner approves this with their PIN.'}</p>
          {held ? <div className="banner warn"><Icon.clock /><div>On hold until {formatDateTime(x.hold_until)}. A manager can end the hold first with <strong>Sell it now</strong> in Inventory.</div></div> : null}
          <Field label="Buyer (person or company)">
            <input className="input" list="wholesale-buyers" value={buyer} onChange={(e) => setBuyer(e.target.value)} autoFocus placeholder="e.g. Bay Area Phone Wholesale" />
          </Field>
          <datalist id="wholesale-buyers">{(buyers.data?.buyers ?? []).map((b) => <option key={b} value={b} />)}</datalist>
          <Field label="Wholesale price" hint={cost !== null ? <Cost cents={cost} label="You paid" /> : undefined}>
            <MoneyInput value={price} onChange={setPrice} />
          </Field>
          {cost !== null && price ? <div className="pill-row"><Cost cents={cost} /><Profit cents={price - cost} size="lg" /></div> : null}
          <WholesalePayPicker paidBy={paidBy} paidRef={paidRef} onPaidBy={setPaidBy} onPaidRef={setPaidRef} />
          <Field label="Note (optional)">
            <input className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. part of a lot of 10" />
          </Field>
        </div>
      )}
    </Sheet>
  );
}

/** "paid in cash", "paid by Zelle · 8841", or "payment not recorded" (wholesales from before it was asked). */
export function wholesalePaidText(w: { paid_by?: string | null; paid_label?: string | null }): string {
  if (!w.paid_by) return 'payment not recorded';
  return w.paid_by === 'cash' ? 'paid in cash' : `paid by ${w.paid_label ?? w.paid_by}`;
}

/** "Sold wholesale to … for …" on the phone's record, with Undo wholesale when `onUndo` is given. */
export function WholesaleInfo({ w, onUndo, soldWholesale }: { w: any; onUndo?: () => void; soldWholesale?: boolean }) {
  // Sold wholesale, seen by someone who isn't the owner: the buyer and the price are the owner's.
  if (!w && soldWholesale) return <div className="banner"><Icon.truck /><div className="grow">Sold wholesale. The buyer and the price are the owner’s to see.</div></div>;
  if (!w) return null;
  return (
    <div className="banner">
      <Icon.truck />
      <div className="grow">
        Sold wholesale to <strong>{w.buyer}</strong> for {formatCents(w.price_cents)} ({wholesalePaidText(w)}) on {formatDateTime(w.created_at)}{w.employee_name ? ` by ${w.employee_name}` : ''}
        {typeof w.cost_cents === 'number' ? <> <Cost cents={w.cost_cents} /></> : null}{typeof w.profit_cents === 'number' ? <> <Profit cents={w.profit_cents} /></> : null}{w.note ? ` · ${w.note}` : ''}
      </div>
      {onUndo ? <Button size="sm" icon="history" onClick={onUndo}>Undo wholesale</Button> : null}
    </div>
  );
}

/**
 * Undo wholesale: the phone comes back the way it was before (its status and price). Cash the
 * buyer paid comes back out of this register's drawer; other payments are marked as given back.
 * The owner does it; anyone else needs the owner's PIN.
 */
export function UndoWholesaleSheet({ deviceId, title, w, onClose, onDone }: { deviceId: string; title: string; w: any; onClose: () => void; onDone: (device: any) => void }) {
  const { can, me } = useSession();
  const { run, busy } = useAction();
  const [reason, setReason] = useState('');
  const cash = w.paid_by === 'cash';
  const go = async () => {
    const r = await run(
      (approval?: ApprovalInput) => post(`/api/devices/${deviceId}/wholesale/undo`, { reason: reason.trim(), approval }),
      { success: cash ? `Undone. Give ${w.buyer} the ${formatCents(w.price_cents)} from the drawer.` : 'Undone. The phone is back in inventory.' },
    );
    if (r) onDone(r);
  };
  return (
    <Sheet
      title="Undo wholesale"
      onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="danger" icon="history" busy={busy} disabled={!reason.trim() || (cash && !me?.register)} onClick={go}>Undo wholesale</Button></>}
    >
      <p><strong>{title}</strong> comes back into inventory the way it was before it went to {w.buyer}.</p>
      <p className={cash && !me?.register ? 'error-text' : undefined}>
        {cash
          ? me?.register ? `${formatCents(w.price_cents)} cash comes out of this register’s drawer to give back to ${w.buyer}.` : `${w.buyer} paid cash, so this is done on the store tablet: the ${formatCents(w.price_cents)} comes out of its drawer.`
          : w.paid_by ? `Give ${w.buyer} back the ${formatCents(w.price_cents)} they paid (${w.paid_label}); it’s marked as given back.` : `Give ${w.buyer} back the ${formatCents(w.price_cents)} they paid.`}
      </p>
      {!can('wholesale.sell') ? <p className="small muted">The owner approves this with their PIN.</p> : null}
      <Field label="Why"><input className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. buyer returned it" autoFocus /></Field>
    </Sheet>
  );
}
